import {useState, useEffect} from 'react';
import {formatCountdownTime} from '../utils/date-time-utils';

export interface CountdownTimerOptions {
  initialSeconds?: number;
  autoStart?: boolean;
  onExpire?: () => void;
}

export interface CountdownTimerReturn {
  timeLeft: number;
  isActive: boolean;
  isExpired: boolean;
  formattedTime: string;
  resetTimer: (seconds?: number) => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  startTimer: () => void;
}

export const useCountdownTimer = (options: CountdownTimerOptions = {}): CountdownTimerReturn => {
  const {initialSeconds = 120, autoStart = true, onExpire} = options;

  const [timeLeft, setTimeLeft] = useState(initialSeconds);
  const [isActive, setIsActive] = useState(autoStart);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (isActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !isExpired) {
      setIsActive(false);
      setIsExpired(true);
      onExpire?.();
    }
  }, [timeLeft, isActive, isExpired, onExpire]);

  const resetTimer = (seconds: number = initialSeconds) => {
    setTimeLeft(seconds);
    setIsActive(autoStart);
    setIsExpired(false);
  };

  const pauseTimer = () => {
    setIsActive(false);
  };

  const resumeTimer = () => {
    if (timeLeft > 0) {
      setIsActive(true);
      setIsExpired(false);
    }
  };

  const startTimer = () => {
    setTimeLeft(initialSeconds);
    setIsActive(true);
    setIsExpired(false);
  };

  return {
    timeLeft,
    isActive,
    isExpired,
    formattedTime: formatCountdownTime(timeLeft),
    resetTimer,
    pauseTimer,
    resumeTimer,
    startTimer,
  };
};
